import { AfterViewInit, Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { animate, AnimationBuilder, style } from '@angular/animations';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, throttleTime } from 'rxjs/operators';

@Directive({ selector: '[upscRotaryCounter]' })
export class RotaryCounterDirective implements OnChanges, AfterViewInit, OnDestroy {
    @Input('upscRotaryCounter') public number: number;
    @Input() public lineHeight = 1.25;
    private previousNumber = 0;
    private resizeSubscription: Subscription;

    constructor(public el: ElementRef,
                public renderer: Renderer2,
                private builder: AnimationBuilder,
    ) {}

    public ngOnInit() {
        this.resizeSubscription = fromEvent(window, 'resize')
            .pipe(
                throttleTime(200),
                debounceTime(200),
            )
            .subscribe(() => {
                // Conditional execution (if needed)
                if (window.innerWidth <= 768) {
                    this.displayUpdatedNumber();
                }
            });
    }

    public ngOnChanges(changes: SimpleChanges) {
        // changes?.number
        // && +changes?.number?.previousValue !== +changes?.number?.currentValue
        // && this.onNumberChanged(changes?.number?.previousValue || 0);

        if (changes?.number) {
            const { previousValue, currentValue } = changes.number;
            if (+previousValue !== +currentValue) {
                this.onNumberChanged(previousValue || 0);
            }
        }
    }

    public ngAfterViewInit(): void {
        this.displayUpdatedNumber();
    }

    public ngOnDestroy() {
        this.resizeSubscription.unsubscribe();
    }

    private onNumberChanged(previousNumber: number): void {
        this.previousNumber = previousNumber !== undefined ? previousNumber : 0;
        this.displayUpdatedNumber();
    }

    private displayUpdatedNumber() {
        if (this.number === undefined) {
            return;
        }

        const numberString = this.number.toString();

        // Remove extra digits if the number of digits has decreased
        for (let i = 0; i < this.el.nativeElement.children.length; i++) {
            if (i >= numberString.length) {
                this.renderer.removeChild(this.el.nativeElement, this.el.nativeElement.children[i]);
            }
        }

        this.createOrUpdateSpans(numberString);

        // Apply flex layout to the host element
        this.renderer.setStyle(this.el.nativeElement, 'display', 'flex');
        this.renderer.setStyle(this.el.nativeElement, 'flex-direction', 'row');
        this.renderer.setStyle(this.el.nativeElement, 'align-items', 'stretch');
        this.renderer.setStyle(this.el.nativeElement, 'justify-content', 'center');
        this.renderer.setStyle(this.el.nativeElement, 'overflow', 'hidden');
        this.renderer.setStyle(this.el.nativeElement, 'height', '1em');
        this.renderer.setStyle(this.el.nativeElement, 'line-height', `${this.lineHeight}`);

        // TODO: Somewhere from this point, sidebar is being forced to automatically expand on load or page redirect (this is causing issues on pages using upsc-dd-remaining-credits)
        // Calculate the height and apply the animations.
        const spanHeight = this.el.nativeElement.offsetHeight;
        this.applyAnimation(numberString, spanHeight);
    }

    private createOrUpdateSpans(numberString: string) {
        // Create new span elements for each digit in the number
        for (let i = 0; i < numberString.length; i++) {
            let container = this.el.nativeElement.children[i];
            if (!container) {
                container = this.createSpanContainer();
                this.renderer.appendChild(this.el.nativeElement, container);
            }

            for (let j = 0; j < 10; j++) {
                let span = container.children[j];
                if (!span) {
                    span = this.createSpan(j);
                    this.renderer.appendChild(container, span);
                }
            }
        }
    }

    private createSpanContainer(): HTMLElement {
        const container = this.renderer.createElement('div');
        this.renderer.setStyle(container, 'position', 'relative');
        this.renderer.setStyle(container, 'height', '1000%');
        this.renderer.setStyle(container, 'aspect-ratio', '1 / calc(1.61803398875 * 11)');
        this.renderer.setStyle(container, 'flex', '0 0 auto');
        this.renderer.setStyle(container, 'display', 'flex');
        this.renderer.setStyle(container, 'flex-direction', 'column');
        this.renderer.setStyle(container, 'align-items', 'center');
        this.renderer.setStyle(container, 'justify-content', 'center');

        return container;
    }

    private createSpan(j: number): HTMLElement {
        const span = this.renderer.createElement('span');
        const text = this.renderer.createText(j.toString());
        this.renderer.appendChild(span, text);
        this.renderer.setStyle(span, 'position', 'absolute');
        this.renderer.setStyle(span, 'top', `${ j * 10 }%`); // Set top to a percentage of the container's height
        this.renderer.setStyle(span, 'height', '10%'); // Set height to 10% of the container's height

        return span;
    }

    private determineDirection(): number {
        // If the previousNumber is null, don't move
        if (this.previousNumber === null) {
            return 0;
        }

        // If the number increased, move up (-1), if it decreased, move down (1), otherwise, don't move (0)
        return this.number > this.previousNumber ? -1 : this.number < this.previousNumber ? 1 : 0;
    }

    private applyAnimation(numberString: string, spanHeight: number) {
        const previousNumberString = (this.previousNumber || 0).toString().padStart(numberString.length, '0');

        // Apply the animations
        for (let i = 0; i < numberString.length; i++) {
            const digit = parseInt(numberString.charAt(i), 10);
            const previousDigit = parseInt(previousNumberString.charAt(i), 10);

            /** BUG **
             In some cases, the initial digit is incorrectly set.
             Skipping the animation may make it display incorrect digit.
             The case we are facing is:
             initial value provided to this directive: 45
             new value from API: 1495
             initial last digit displayed: 0 (which should be 5 for 45)
             With that situation, the last digits for the previous and current values are the same (5),
             and thus no animation is played and the number stays 0.
             We need to find out why the last digit is initially set to 0.

             More observations:
             initial value provided to this directive: 44
             new value from API: 1495
             initial last digit displayed: 5  <== works fine!

             initial value provided to this directive: 95
             new value from API: 1495
             initial last 2 digits displayed: 0 and 0 (which should be 9 and 5 for 95)

             So it seems like if any digit of the initial value matches the new value,
             those digits will remain 0 instead of the correct initial digit.
             Preventing the animation on an unchanged digit thus makes it stay 0.

             It's worth noting that it works just fine if the initial value provided to this directive is undefined.
             Please take this as a note for a future debugging.

             This `true` flag below is set to force the animation so that it animates to the target value
             regardless of whether the digit is changed.
             Weird enough, if we force the animation, the initial value of the digit seems to be correctly set to 5.
             */
            const shouldAnimateDigit = digit !== previousDigit || true;

            if (shouldAnimateDigit) {
                const container = this.el.nativeElement.children[i];

                // Calculate the delay based on the digit's position
                const delay = (numberString.length - i - 1) * 300; // Adjust the multiplier to change the delay between digits

                // Create the animation
                const startState = previousDigit * spanHeight;
                const endState = (digit * spanHeight) + 0.5;

                const bounce = 'cubic-bezier(0.68, -0.2, 0.27, 1.2)';
                const factory = this.builder.build([
                    style({ transform: `translateY(-${ startState }px)` }),
                    animate(`1.5s ${ delay }ms ${ bounce }`, style({ transform: `translateY(-${ endState }px)` })),
                ]);

                const player = factory.create(container);
                player.play();
            }
        }
    }
}
